import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Label,
  Button,
  Input,
  ModalHeader,
  ModalFooter,
  Modal,
  Card, Spinner
} from "reactstrap";
import Select from "react-select";
import DeviceCard from "./DeviceCard";
import { useDataWithRoute, useTitleFromComponent } from "../../utils/hooks";
import { useVsiAppContext } from "../../stores/VsiApp";
import Nouns from "./CollectionDeviceInputs/Nouns";
import { PostAuth } from "../../utils/Verbs";
import { useNavigate } from "react-router-dom";
import { VsiBubble, VsiH4, VsiHr, VsiSpanText } from "../../styled/App.styled";
import VsiLabeledSelect from "../../components/VsiLabeledSelect";
import { validateIp } from "../../utils/Helpers";

const hitachiOpsDeviceOptions = [
  { label: "Common Services", value: "commonServices" },
  { label: "Administrator/Analyzer Device", value: "adminAnalyzerDevice" }
];

export default function AddCollectionDevice({ title }) {
  useTitleFromComponent(title);
  const {
    client,
    user,
    theme,
    getAccessTokenSilently
  } = useVsiAppContext();
  const navigate = useNavigate();
  const [allUserNames, setAllUserNames] = useState(client.username);
  const [allPasswords, setAllPassword] = useState();
  const [allConfirmPasswords, setAllConfirmPassword] = useState();
  const [allCLIPath, setAllCLIPath] = useState("");
  const [allTimeout, setAllTimeout] = useState("");
  const [allCollectionDays, setAllCollectionDays] = useState([]);
  const [allCollectionTime, setAllCollectionTime] = useState("09:00");
  const [allSiteCode, setAllSiteCode] = useState("");
  const [showCreateBtn, setShowCreateBtn] = useState(false);
  const [isMultipleDevices, setIsMultipleDevices] = useState(false);
  const [devices, setDevices] = useState([]);
  const [isAddingDevice, setIsAddingDevice] = useState(false);
  const createDevice = (deviceType) => {
    return {
      ip_address: "",
      username: allUserNames || client.username,
      password: allPasswords,
      confirmPassword: allConfirmPasswords,
      ssh_key: "",
      site_code: allSiteCode || "",
      device_alias: "",
      array_name: "",
      collection_day: "",
      collectionDays: allCollectionDays || {},
      collection_time: allCollectionTime || "09:00",
      client_id: client.client_id,
      user_id: client.user_id,
      device_type_code: deviceType,
      collects_performance: 0,
      serial_number: "",
      cli_path: "",
      port: "",
      api_token: "",
      hitachi_ops: "",
      selected_rule: Nouns.createNewEmptyRule(),
      timeout: allTimeout || "",
      is_valid: false,
      errorMessage: "",
      usesCliPath: Nouns.cliDevices.includes(deviceType),
      isCompellent: deviceType.includes("COMPELLENT"),
      timeoutDisabled: true,
      enabled: true
    };
  };

  const [newDeviceTypes, setNewDeviceTypes] = useState([]);
  const [deviceTypeAndNum, setDeviceTypeAndNum] = useState({});
  const [hitachiOpsDeviceOption, setHitachiOpsDeviceOption] = useState("");
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [{ recordset: deviceTypes }] = useDataWithRoute(
    `/api/v6/admin/device_types`
  );
  const [cards, setCards] = useState([]);

  const deviceTypesList = useMemo(() => {
    if (deviceTypes?.length) {
      return deviceTypes.map(dt => {
        return { label: dt.device_type_code.trim(), value: dt.device_type_code.trim() };
      });
    }
    return [];
  }, [deviceTypes]);

  useEffect(() => {
    setCards(
      devices.map((d, index) => {
        return createDeviceCard(d, setDeviceCallback, index);
      })
    );
  }, [devices]);

  useEffect(() => {
    renderDeviceCards();
  }, [deviceTypeAndNum]);

  useEffect(() => {
    const newDevices = devices.map((d) => {
      return {
        ...d,
        username: allUserNames,
        password: allPasswords,
        confirmPassword: allConfirmPasswords,
        cli_path: allCLIPath,
        collection_day: allCollectionDays?.map(({ value }) => value)?.join(","),
        collectionDays: allCollectionDays,
        collection_time: allCollectionTime,
        timeout: allTimeout,
        site_code: allSiteCode
      };
    });
    setDevices(newDevices);
    shouldRenderCreateBtn(newDevices);
  }, [
    allUserNames,
    allPasswords,
    allCLIPath,
    allCollectionDays,
    allCollectionTime,
    allConfirmPasswords,
    allTimeout,
    allSiteCode
  ]);

  const handleMultiSelectChange = (selectItems) => {
    setNewDeviceTypes(selectItems);
    let dtn = {};
    selectItems.forEach((dt) => {
      dtn[dt.value] = 1;
    });
    setDeviceTypeAndNum(dtn);
    setIsMultipleDevices(selectItems.length > 1);
  };

  const containsDeviceType = (deviceType) => {
    let flag = false;
    newDeviceTypes.forEach((dt) => {
      if (dt.value === deviceType) {
        flag = true;
      }
    });
    return flag;
  };

  const handleHitachiOpsSelect = (selectItem) => {
    setHitachiOpsDeviceOption(selectItem);
    setDevices(
      devices.map((d) => {
        return { ...d, hitachi_ops: selectItem.value };
      })
    );
  };

  const handleAllCollectionDaysChange = (selectedItems) => {
    setAllCollectionDays(selectedItems);
  };

  const onFormSubmit = async (event) => {
    event.disabled = true;
    event.preventDefault();
    const token = await getAccessTokenSilently();
    let devicePayload;
    let route = "/api/v6/admin/collection_devices";
    if (devices.length === 1) {
      devicePayload = {
        ...devices[0],
        client_id: client.clientId,
        user_id: user._id
      };
    } else if (devices.length > 1) {
      devicePayload = devices.map((device) => {
        return {
          ...device,
          client_id: client.clientId,
          user_id: user._id
        };
      });
      route += "_bulk";
    } else {
      return;
    }
    setIsAddingDevice(true);
    try {
      const createdDevices = await PostAuth(
        route,
        devicePayload,
        token
      );

      if (createdDevices.status === 201) {
        setIsAddingDevice(false);
        navigate("/user/settings/collection-devices");
      } else {
        console.error(createdDevices);
        setIsAddingDevice(false);
      }
    } catch (e) {
      console.error(e);
      setIsAddingDevice(false);
    }
  };

  const onDeviceNumberChange = (event) => {
    const { name, value } = event.target;
    setDeviceTypeAndNum({ ...deviceTypeAndNum, [name]: value });
    setIsMultipleDevices(value > 1 || Object.keys(deviceTypeAndNum) > 1);
  };

  const renderDeviceCards = () => {
    let devicesObj = [];
    Object.keys(deviceTypeAndNum).forEach((key) => {
      let hitachiOps;
      if (hitachiOpsDeviceOption === "adminAnalyzerDevice") {
        hitachiOps = ["Administrator", "Analyzer"];
      }
      for (let i = 0; i < deviceTypeAndNum[key]; i++) {
        devicesObj.push(createDevice(key));
      }
    });
    setDevices(devicesObj);
  };

  const createDeviceCard = (device, setDeviceCallback, index) => {
    return (
      <DeviceCard
        // ref={(element) => (collectionDevicesRefs.current[numCards - 1] = element)}
        key={`${device.device_type}-device-card-${index}`}
        device={device}
        setDeviceCallback={setDeviceCallback}
        index={index}
      />
    );
  };

  const setDeviceCallback = (device, index) => {
    let devicesObj = [...devices];
    devicesObj[index] = { ...device };
    setDevices(devicesObj);
    shouldRenderCreateBtn(devicesObj);
  };

  const shouldRenderCreateBtn = (devicesObj) => {
    let flag = newDeviceTypes?.length > 0;
    newDeviceTypes?.forEach((dt) => {
      if (dt.value.includes("HITACHI_OPS")) {
        if (hitachiOpsDeviceOption.length < 1) {
          flag = false;
        }
      }
    });
    devicesObj.forEach((device) => {
      // it validates ip/pass by default; it customizes by device
      let validity =
        (
          device.password === device.confirmPassword &&
          validateIp(device.ip_address) &&
          device.collectionDays.length > 0 &&
          !Nouns.TokenInVaultDevices.includes(device?.device_type_code)
        ) ||
        (
          Nouns.TokenInVaultDevices.includes(device?.device_type_code) &&
          validateIp(device.ip_address) &&
          (typeof device.password !== "undefined" && device.password.trim() !== "") &&
          device.collectionDays.length > 0
        ) ||
        (
          Nouns.FileAnalysisDevice.includes(device?.device_type_code) &&
          device?.cli_path.length > 1 &&
          device.collectionDays.length > 0
        ) ||
        (
          device?.device_type_code.includes("GROWTH_RATE_CALC") &&
          device.collectionDays.length > 0
        ) ||
        (
          device?.device_type_code.includes("GOOGLE_CLOUD_DEVICE") &&
          device?.cli_path.length > 0 &&
          device.collectionDays.length > 0
        ) ||
        (
          device?.device_type_code.includes("POWERMAX_DEVICE") &&
          device.serial_number.length > 1 &&
          validateIp(device.ip_address) &&
          device.collectionDays.length > 0
        );

      if (!validity) {
        flag = false;
      }
    });
    setShowCreateBtn(flag);
  };

  const containsCLIDevice = () => {
    let flag = false;
    newDeviceTypes?.forEach((dt) => {
      if (Nouns.cliDevices.includes(dt.value)) {
        flag = true;
      }
    });
    return flag;
  };

  return (
    <Card className={`report-card p-2 nav-card-${theme}`}>
      <VsiH4>Add Collection Device</VsiH4>
      <VsiHr />
      <VsiBubble noAutobot>
        <Row>
          <Col>
            <div style={{ display: "flex" }}>
              <Label htmlFor="device_type_code">
                <VsiSpanText>Select Device Type(s) to be added</VsiSpanText>
              </Label>
              <Button
                color={"secondary"}
                outline
                size="md"
                onClick={() => {
                  navigate("/user/settings/collection-devices");
                }}
                style={{ marginLeft: "auto", marginBottom: 10 }}
              >
                Cancel
              </Button>
            </div>
            <div style={{ color: "black" }}>
              <VsiLabeledSelect
                minWidth={"100%"}
                value={newDeviceTypes}
                options={deviceTypesList}
                name="device_type_code"
                onChange={handleMultiSelectChange}
                isMulti
              />
            </div>
          </Col>
        </Row>
      </VsiBubble>
      {containsDeviceType("HITACHI_OPS_DEVICE") ? (
        <VsiBubble noAutobot>
          <Row>
            <Col lg={6} md={6} style={{ marginTop: 10 }}>
              <Label htmlFor="hitachiOpsDeviceOption">
                Hitachi Device Option
              </Label>
              <div style={{ color: "black" }}>
                <Select
                  options={hitachiOpsDeviceOptions}
                  name="hitachiOpsDeviceOption"
                  value={hitachiOpsDeviceOption}
                  onChange={handleHitachiOpsSelect}
                />
              </div>
            </Col>
          </Row>
        </VsiBubble>
      ) : null}
      <VsiBubble noAutobot>
        <Row>
          {Object.entries(deviceTypeAndNum).map(([key, value]) => {
            return (
              <Col style={{ marginTop: 0 }} key={`${key}-col`} lg={1} md={1}>
                <Label htmlFor={key}>{key}</Label>
                <Input
                  type="number"
                  name={key}
                  value={value}
                  onChange={onDeviceNumberChange}
                />
              </Col>
            );
          })}
        </Row>
      </VsiBubble>
      {isMultipleDevices ? (
        <VsiBubble noAutobot>
          <Row>
            <Col sm={4} xs={12}>
              <Label htmlFor="username">Update All Usernames</Label>
              <Input
                type="text"
                name="username"
                onChange={(event) => {
                  setAllUserNames(event.target.value);
                }}
                value={allUserNames}
              />
            </Col>
            <Col sm={4} xs={12}>
              <Label htmlFor="password">Update All Passwords</Label>
              <Input
                type="password"
                name="password"
                onChange={(event) => {
                  setAllPassword(event.target.value);
                }}
                value={allPasswords}
              />
            </Col>
            <Col sm={4} xs={12}>
              <Label htmlFor="confirmPassword">Confirm All Passwords</Label>
              <Input
                type="password"
                name="confirmPassword"
                onChange={(event) => {
                  setAllConfirmPassword(event.target.value);
                }}
                value={allConfirmPasswords}
              />
            </Col>

            {containsCLIDevice ? (
              <Row>
                <Col xs={12} sm={6}>
                  <Label htmlFor="cli_path">All CLI Path</Label>
                  <Input
                    type="text"
                    name="cli_path"
                    onChange={(event) => {
                      setAllCLIPath(event.target.value);
                    }}
                    value={allCLIPath}
                  />
                </Col>
              </Row>
            ) : null}
          </Row>
          <Row style={{ marginBottom: 10 }}>
            <Col lg={3} sm={6} xs={12}>
              <Label htmlFor="allSiteCode">All Collection Machine (Optional)</Label>
              <Input
                type="text"
                value={allSiteCode}
                name="AllSiteCode"
                onChange={(event) => {
                  setAllSiteCode(event.target.value);
                }}
              />
            </Col>
            <Col lg={3} sm={6} xs={12}>
              <Label htmlFor="collectionDays">
                Change All Collection Day(s)
              </Label>
              <div style={{ color: "black" }}>
                <VsiLabeledSelect
                  minWidth={"100%"}
                  value={allCollectionDays}
                  options={[
                    { value: "mon", label: "Monday" },
                    { value: "tue", label: "Tuesday" },
                    { value: "wed", label: "Wednesday" },
                    { value: "thurs", label: "Thursday" },
                    { value: "fri", label: "Friday" },
                    { value: "sat", label: "Saturday" },
                    { value: "sun", label: "Sunday" }
                  ]}
                  name="collectionDays"
                  isMulti
                  onChange={handleAllCollectionDaysChange}
                />
              </div>
            </Col>
            <Col lg={3} sm={6} xs={12}>
              <Label htmlFor="collectionTime">Change All Collection Time</Label>
              <Input
                id={"collectionTime"}
                type="time"
                size="large"
                required={false}
                name="collectionTime"
                value={allCollectionTime}
                // className={'form-control'}
                onChange={(event) => {
                  setAllCollectionTime(event.target.value);
                }}
                step={300}
              />
            </Col>
            <Col sm={6} xs={12} lg={3}>
              <Label htmlFor="alltimeout">Collection Timeout (hrs)</Label>
              <Input
                value={allTimeout}
                name="alltimeout"
                type="number"
                id={`all-device-timeout`}
                onChange={(event) => {
                  setAllTimeout(event.target.value);
                }}
              />
            </Col>
          </Row>
        </VsiBubble>
      ) : null}

      {cards}

      {showCreateBtn ? (
        <Row>
          <Col sm={12} xs={12}>
            <Button
              onClick={onFormSubmit}
              color="success"
              className="create-device-btn"
            >
              {isAddingDevice ? <Spinner size={"sm"} /> : "Create"}
            </Button>
          </Col>
        </Row>
      ) : null}

      <Modal
        className="warning-modal"
        backdrop="static"
        isOpen={showWarningModal}
      >
        <ModalHeader toggle={() => setShowWarningModal(false)}>
          Password and confirm password do not match.
        </ModalHeader>
        <ModalFooter>
          <Button onClick={() => setShowWarningModal(false)} color="dark">
            Ok
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}
